import { Box } from "@primer/react";
import DataGrid, { Column, FilterRow, Grouping, HeaderFilter, Paging, Selection, Sorting } from "devextreme-react/data-grid";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ganMienGiamApi } from "../../../api/ganMienGiamApi";
import { Button } from "../../../components/buttons";
import { AnimationPopup } from "../../../components/modal";
import ModalActions from "../../../components/ui/modal/ModalActions";
import { showNotify } from "../../../helpers/toast";
import { RootState } from "../../../state/reducers";

type GanMienGiamEditModalProps = {
    onClose: () => void;
    onSuccess: () => void;
    animationOf: string;
    nam_hoc: string;
    ts_hocsinh_id: number;
    ho_ten: string;
    ma_hs: string;
}

export const GanMienGiamEditModal = (props: GanMienGiamEditModalProps) => {
    const hinhThucGridRef = useRef<any>();
    const [selectedHinhThucIds, setSelectedHinhThucIds] = useState<number[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedHinhThucCount, setSelectedHinhThucCount] = useState(0);
    const state = useSelector((x: RootState) => x.ganMienGiam);
    const { sf_hinhthucmiengiams, sf_dexuatmiengiams } = state;
    useEffect(() => {
        if (props.ts_hocsinh_id && sf_dexuatmiengiams) {
            const hocSinhMiengiams = sf_dexuatmiengiams.filter(
                (x: any) => x.ts_hocsinh_id === props.ts_hocsinh_id
            );
            const hinhThucIds = hocSinhMiengiams.map((x: any) => x.sf_hinhthucmiengiam_id);
            setSelectedHinhThucIds(hinhThucIds);
            setSelectedHinhThucCount(hinhThucIds.length);
            
            if (hinhThucGridRef.current && hinhThucIds.length > 0) {
                setTimeout(() => {
                    if (hinhThucGridRef.current) {
                        const instance = hinhThucGridRef.current.instance;
                        instance.selectRows(hinhThucIds, false);
                    }
                }, 200);
            }
        }
    }, [props.ts_hocsinh_id, sf_dexuatmiengiams]);

    const onClose = useCallback(() => {
        window.animationHidePopup(props.animationOf || "");
        setTimeout(() => {
            props.onClose();
        }, 500);
    }, [props.animationOf, props.onClose]);

    const handleSave = async () => {
        if (selectedHinhThucIds.length === 0) {
            showNotify({ message: "Vui lòng chọn ít nhất một hình thức miễn giảm", type: "warning" });
            return;
        }

        setIsSaving(true);
        try {
            const result = await ganMienGiamApi.batchInsert({
                ts_hocsinh_ids: [props.ts_hocsinh_id],
                sf_hinhthucmiengiam_ids: selectedHinhThucIds,
                nam_hoc: props.nam_hoc,
                so_lan_ap_dung: 1
            });
            
            if (result.is_success) {
                showNotify({ message: "Cập nhật miễn giảm thành công", type: "success" });
                props.onSuccess();
            } else {
                showNotify({ message: "Có lỗi xảy ra khi cập nhật miễn giảm", type: "error" });
            }
        } catch (error) {
            showNotify({ message: "Có lỗi xảy ra", type: "error" });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <AnimationPopup
            title={`Chỉnh sửa miễn giảm - ${props.ma_hs} - ${props.ho_ten}`}
            animationOf={props.animationOf}
            onClose={onClose}
            width="70%"
        >
            <Box sx={{ border: '1px solid', borderColor: 'border.default', borderRadius: 2, p: 2 }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ fontWeight: 'bold' }}>Chọn hình thức miễn giảm</Box>
                    {selectedHinhThucCount > 0 && (
                        <Box sx={{ fontSize: '12px', color: 'success.fg', fontWeight: 'bold' }}>
                            Đã chọn: {selectedHinhThucCount} hình thức
                        </Box>
                    )}
                </Box>
                {!sf_hinhthucmiengiams || sf_hinhthucmiengiams.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center', color: 'fg.muted' }}>
                        Không có hình thức miễn giảm nào
                    </Box>
                ) : (
                    <DataGrid
                        ref={hinhThucGridRef}
                        dataSource={sf_hinhthucmiengiams}
                        keyExpr="id"
                        height={500}
                        showBorders={true}
                        columnAutoWidth={false}
                        noDataText="Không có dữ liệu"
                        showRowLines={true}
                        showColumnLines={true}
                        wordWrapEnabled={true}
                        rowAlternationEnabled={true}
                        hoverStateEnabled={true}
                        onRowPrepared={(e: any) => {
                            if (e.rowType === 'group') {
                                const checkboxCell = e.rowElement?.querySelector('.dx-select-checkbox');
                                if (checkboxCell && checkboxCell instanceof HTMLElement) {
                                    checkboxCell.style.display = 'none';
                                }
                            }
                        }}
                        onSelectionChanged={(e: any) => {
                            if (hinhThucGridRef.current) {
                                const instance = hinhThucGridRef.current.instance;
                                const selectedRows = instance.getSelectedRowsData();
                                
                                const hinhThucRows = selectedRows.filter((row: any) => row && row.id !== undefined);
                                
                                const nhomMap = new Map<number, any>();
                                const rowsToDeselect: any[] = [];
                                
                                hinhThucRows.forEach((row: any) => {
                                    const nhomId = row.nhom_mien_giam;
                                    if (nhomMap.has(nhomId)) {
                                        rowsToDeselect.push(nhomMap.get(nhomId));
                                        nhomMap.set(nhomId, row);
                                    } else {
                                        nhomMap.set(nhomId, row);
                                    }
                                });
                                
                                if (rowsToDeselect.length > 0) {
                                    rowsToDeselect.forEach((row: any) => {
                                        instance.deselectRows([row.id]);
                                    });
                                    const finalSelectedRows = instance.getSelectedRowsData();
                                    const finalHinhThucRows = finalSelectedRows.filter((row: any) => row && row.id !== undefined);
                                    const hinhThucIds = finalHinhThucRows.map((row: any) => row.id).filter((id: any) => id && !isNaN(id));
                                    setSelectedHinhThucIds(hinhThucIds);
                                    setSelectedHinhThucCount(finalHinhThucRows.length);
                                } else {
                                    const hinhThucIds = hinhThucRows.map((row: any) => row.id).filter((id: any) => id && !isNaN(id));
                                    setSelectedHinhThucIds(hinhThucIds);
                                    setSelectedHinhThucCount(hinhThucRows.length);
                                }
                            }
                        }}
                    >
                        <Selection mode="multiple" showCheckBoxesMode="always" />
                        <Grouping autoExpandAll={true} />
                        <FilterRow visible={true} />
                        <HeaderFilter visible={true} />
                        <Sorting mode="multiple" />
                        <Paging enabled={false}/>
                        <Column 
                            dataField="nhom_mien_giam" 
                            caption="Nhóm" 
                            width={100}
                            groupIndex={0}
                            groupCellRender={(data: any) => {
                                return `Nhóm ${data.value}`;
                            }}
                        />
                        <Column 
                            dataField="ma_hinh_thuc" 
                            caption="Mã" 
                            width={120}
                        />
                        <Column 
                            dataField="hinh_thuc_mien_giam" 
                            caption="Hình thức miễn giảm" 
                        />
                    </DataGrid>
                )}
            </Box>

            <ModalActions>
                <Button type="normal" icon="fa fa-times" text="Đóng" onClick={onClose} />
                <Button
                    type="normal"
                    icon="fas fa-check-square"
                    text="Lưu"
                    onClick={handleSave}
                    isLoading={isSaving}
                />
            </ModalActions>
        </AnimationPopup>
    );
};
